import { useState, useMemo, useEffect } from 'react';
import { AutocompleteValue } from '@mui/material';
import { throttle } from 'lodash';

import { CustomerBase } from '~services/customer/types';
import { searchCustomers } from '~services/customer';
import { executeAsyncRequest } from '~utils/request';

export type CustomerPrediction = Pick<
  CustomerBase,
  | 'customerId'
  | 'legalName'
  | 'code'
  | 'address'
  | 'latitude'
  | 'longitude'
  | 'customerType'
>;

export type CustomersAutocompleteService = (
  params: { input: string },
  callback: (result: CustomerPrediction[]) => void,
) => void;

export interface CustomersAutocompleteParams<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
> {
  initialValue?: string;
  multiple?: Multiple;
  disableClearable?: DisableClearable;
  freeSolo?: FreeSolo;
}

export interface CustomersAutocompleteReturn<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
> {
  value: AutocompleteValue<
    CustomerPrediction,
    Multiple,
    DisableClearable,
    FreeSolo
  >;
  setValue: (
    newValue: AutocompleteValue<
      CustomerPrediction,
      Multiple,
      DisableClearable,
      FreeSolo
    >,
  ) => void;
  inputValue: string;
  setInputValue: (newInputValue: string) => void;
  options: readonly CustomerPrediction[];
  setOptions: (newOptions: readonly CustomerPrediction[]) => void;
  loading: boolean;
}

export const useCustomersAutocomplete = <
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>({
  initialValue,
  multiple,
}: CustomersAutocompleteParams<
  Multiple,
  DisableClearable,
  FreeSolo
>): CustomersAutocompleteReturn<Multiple, DisableClearable, FreeSolo> => {
  const [value, setValue] = useState(
    (multiple ? [] : null) as AutocompleteValue<
      CustomerPrediction,
      Multiple,
      DisableClearable,
      FreeSolo
    >,
  );
  const [inputValue, setInputValue] = useState<string>('');
  const [options, setOptions] = useState<readonly CustomerPrediction[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useMemo(
    () =>
      throttle<CustomersAutocompleteService>((params, callback) => {
        executeAsyncRequest({
          request: searchCustomers,
          values: {
            params: {
              pagination: { page: 1, sizePerPage: 20 },
              filters: { query: params.input, customerTypeList: null },
            },
          },
          onPrefetch: () => {
            setLoading(true);
          },
          onSuccess: (response) => {
            if (response.data?.data.results) {
              const results: CustomerPrediction[] =
                response.data.data.results.map((result) => ({
                  customerId: result.customerId,
                  legalName: result.legalName,
                  code: result.code,
                  address: result.address,
                  latitude: result.latitude,
                  longitude: result.longitude,
                  customerType: result.customerType,
                }));

              callback(results);
            }
          },
          onError: (err) => {
            console.log('Error customer autocomplete');
            console.error(err);
          },
          onPostFetch: () => {},
          onFinally: () => {
            setLoading(false);
          },
        });
      }, 500),
    [],
  );

  useEffect(() => {
    let active = true;

    if (inputValue === '') {
      setOptions(
        (value !== null
          ? multiple
            ? value
            : [value]
          : []) as CustomerPrediction[],
      );
      return undefined;
    }

    if (inputValue.length >= 2) {
      fetch({ input: inputValue }, (results) => {
        if (active) {
          let newOptions: readonly CustomerPrediction[] = [];

          if (value) {
            newOptions = (
              value !== null ? (multiple ? value : [value]) : []
            ) as CustomerPrediction[];
          }

          if (results.length > 0) {
            newOptions = [...newOptions, ...results];
          }

          setOptions(newOptions);
        }
      });
    }

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch, multiple]);

  useEffect(() => {
    if (initialValue && !multiple) {
      setInputValue(initialValue);

      fetch({ input: initialValue }, (results) => {
        let newOptions: readonly CustomerPrediction[] = [];

        if (results.length > 0) {
          newOptions = [...newOptions, ...results];

          setValue(
            results[0] as AutocompleteValue<
              CustomerPrediction,
              Multiple,
              DisableClearable,
              FreeSolo
            >,
          );
        }

        setOptions(newOptions);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, multiple]);

  return {
    value,
    setValue,
    inputValue,
    setInputValue,
    options,
    setOptions,
    loading,
  };
};
